/**
 * Design Resources Component - Frontend Filtering
 *
 * All filtering and pagination happens on the frontend
 * Data is fetched once from REST API
 *
 * @package Empower Genesis
 */

import { SITE_URL } from './constants';

export function designResources() {
  const component = document.querySelector('[data-component="design-resources"]');

  if (!component) return;

  // Component elements
  const elements = {
    filterBtn: component.querySelector('[data-action="toggle-filter"]'),
    filterPanel: component.querySelector('[data-element="filter-panel"]'),
    closeFilterBtn: component.querySelector('[data-action="close-filter"]'),
    resourcesList: component.querySelector('[data-element="resources-list"]'),
    pagination: component.querySelector('[data-element="pagination"]'),
    loading: component.querySelector('[data-element="loading"]'),
    tagSearchInput: component.querySelector('[data-element="tag-search-input"]'),
    tagSuggestions: component.querySelector('[data-element="tag-suggestions"]'),
    selectedTags: component.querySelector('[data-element="selected-tags"]'),
    resourceTypes: component.querySelector('[data-element="resource-types"]'),
    popup: document.querySelector('[data-element="popup"]'),
    popupForm: document.querySelector('[data-element="popup-form"]'),
    thumbnails: component.querySelectorAll('.design-resource-item__thumbnail-link'),
  };

  // State
  const state = {
    allResources: [], // All resources from API
    filteredResources: [], // After applying filters
    filterData: null, // Resource types and tags from API
    currentPage: 1,
    perPage: parseInt(component.dataset.perPage, 10) || 12,
    selectedTypes: [],
    selectedTags: [],
    searchQuery: '',
  };

  /**
   * Fetch all data from REST API
   */
  async function fetchAllData() {
    showLoading();

    const restUrl = component.dataset.restUrl;

    try {
      const response = await fetch(restUrl);
      const data = await response.json();

      if (data.success) {
        state.allResources = data.data.resources;
        state.filterData = data.data.filter_data;
        state.filteredResources = [...state.allResources];

        // Render initial state
        renderFilterTypes();
        renderResources();
        renderPagination();
      } else {
        console.error('Error fetching resources:', data);
        showError();
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
      showError();
    } finally {
      hideLoading();
    }
  }

  /**
   * Show loading overlay
   */
  function showLoading() {
    if (elements.loading) {
      elements.loading.style.display = 'flex';
    }
  }

  /**
   * Hide loading overlay
   */
  function hideLoading() {
    if (elements.loading) {
      elements.loading.style.display = 'none';
    }
  }

  /**
   * Show error message
   */
  function showError() {
    if (elements.resourcesList) {
      elements.resourcesList.innerHTML =
        '<div class="design-resources-empty"><p>Error loading resources. Please try again later.</p></div>';
    }
  }

  /**
   * Toggle filter panel
   */
  function toggleFilter() {
    const isVisible = elements.filterPanel.style.display !== 'none';
    elements.filterPanel.style.display = isVisible ? 'none' : 'block';
  }

  /**
   * Close filter panel
   */
  function closeFilter() {
    elements.filterPanel.style.display = 'none';
  }

  /**
   * Render filter type buttons
   */
  function renderFilterTypes() {
    if (!elements.resourceTypes || !state.filterData) return;

    const html = state.filterData.resource_types
      .map(
        (type) => `
      <button
        class="design-resources-filter__type-btn"
        data-type-id="${type.id}"
        data-type-slug="${type.slug}"
        data-action="toggle-type"
      >
        ${type.name}
      </button>
    `
      )
      .join('');

    elements.resourceTypes.innerHTML = html;

    // Attach event listeners
    elements.resourceTypes.querySelectorAll('[data-action="toggle-type"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const typeId = parseInt(btn.dataset.typeId, 10);
        toggleType(typeId);
      });
    });
  }

  /**
   * Apply filters to resources
   */
  function applyFilters() {
    let filtered = [...state.allResources];

    // Filter by resource types
    if (state.selectedTypes.length > 0) {
      filtered = filtered.filter((resource) => {
        return resource.resource_types.some((type) => state.selectedTypes.includes(type.id));
      });
    }

    // Filter by resource tags
    if (state.selectedTags.length > 0) {
      filtered = filtered.filter((resource) => {
        return resource.resource_tags.some((tag) => state.selectedTags.includes(tag.id));
      });
    }

    // Filter by search query (title)
    if (state.searchQuery) {
      const query = state.searchQuery.toLowerCase();
      filtered = filtered.filter((resource) => {
        return resource.title.toLowerCase().includes(query);
      });
    }

    state.filteredResources = filtered;
    state.currentPage = 1; // Reset to first page
  }

  /**
   * Render resources list
   */
  function renderResources() {
    if (!elements.resourcesList) return;

    if (state.filteredResources.length === 0) {
      elements.resourcesList.innerHTML =
        '<div class="design-resources-empty"><p>No resources found.</p></div>';
      return;
    }

    // Calculate pagination
    const startIndex = (state.currentPage - 1) * state.perPage;
    const endIndex = startIndex + state.perPage;
    const pageResources = state.filteredResources.slice(startIndex, endIndex);

    // Clear list
    elements.resourcesList.innerHTML = '';

    // Render resources using template
    pageResources.forEach((resource) => {
      const itemElement = renderResourceItem(resource);
      elements.resourcesList.appendChild(itemElement);
    });
  }

  /**
   * Render single resource item using template
   */
  function renderResourceItem(resource) {
    const template = document.getElementById('design-resource-item-template');
    if (!template) {
      console.error('Template #design-resource-item-template not found');
      return null;
    }

    // Clone template
    const clone = template.content.cloneNode(true);
    const item = clone.querySelector('.design-resource-item');

    // Set resource ID
    item.setAttribute('data-resource-id', resource.id);

    // Resource types
    const typeElement = item.querySelector('.design-resource-item__type');
    if (resource.resource_types && resource.resource_types.length > 0) {
      const typesString = resource.resource_types.map((t) => t.name).join(', ');
      typeElement.textContent = typesString;
    } else {
      typeElement.remove();
    }

    // Title
    const titleElement = item.querySelector('.design-resource-item__title');
    titleElement.textContent = resource.title;

    // Tags
    const tagsContainer = item.querySelector('.design-resource-item__tags');
    if (resource.resource_tags && resource.resource_tags.length > 0) {
      tagsContainer.innerHTML = '';
      resource.resource_tags.forEach((tag) => {
        const tagSpan = document.createElement('span');
        tagSpan.className = 'design-resource-item__tag';
        tagSpan.textContent = tag.name;
        tagsContainer.appendChild(tagSpan);
      });
    } else {
      tagsContainer.remove();
    }

    // Button
    const btnLink = item.querySelector('.design-resource-item__btn');
    const btnText = item.querySelector('.design-resource-item__btn-text');

    btnLink.href = resource.link;
    btnLink.classList.add(`design-resource-item__btn--${resource.link_type}`);

    // Button text
    let buttonText = 'Download';
    if (resource.link_type === 'request') {
      buttonText = 'Request Download';
    } else if (resource.link_type === 'lead') {
      buttonText = 'Access Download';
    } else if (resource.link_type === 'external') {
      buttonText = 'View Resource';
    }
    btnText.textContent = buttonText;

    // Target attribute
    if (resource.link_type !== 'internal') {
      btnLink.setAttribute('target', '_blank');
      btnLink.setAttribute('rel', 'noopener noreferrer');
    }

    // Thumbnail
    const thumbnailContainer = item.querySelector('.design-resource-item__thumbnail');
    const thumbnailLink = item.querySelector('.design-resource-item__thumbnail-link');

    if (resource.is_popup) {
      item.classList.add('with-popup');
      if ('video' === resource.popup_type) {
        item.classList.add('with-popup--video');
      } else {
        item.classList.add('with-popup--image');
      }
    }

    if (resource.thumbnail) {
      thumbnailLink.href = resource.link;
      const img = thumbnailContainer.querySelector('img');
      img.src = resource.thumbnail.url;
      img.alt = resource.thumbnail.alt || resource.title;
      thumbnailContainer.style.display = '';
    } else {
      thumbnailContainer.remove();
    }

    return clone;
  }

  /**
   * Render pagination
   */
  function renderPagination() {
    if (!elements.pagination) return;

    const totalPages = Math.ceil(state.filteredResources.length / state.perPage);

    if (totalPages <= 1) {
      elements.pagination.innerHTML = '';
      return;
    }

    const current = state.currentPage;
    const range = 2;
    const start = Math.max(1, current - range);
    const end = Math.min(totalPages, current + range);

    let pagesHtml = '';

    // First page
    if (start > 1) {
      pagesHtml += `<button class="design-resources-pagination__page" data-action="goto-page" data-page="1">1</button>`;
      if (start > 2) {
        pagesHtml += `<span class="design-resources-pagination__ellipsis">...</span>`;
      }
    }

    // Page range
    for (let i = start; i <= end; i++) {
      const activeClass = i === current ? 'design-resources-pagination__page--active' : '';
      const disabled = i === current ? 'disabled' : '';
      pagesHtml += `<button class="design-resources-pagination__page ${activeClass}" data-action="goto-page" data-page="${i}" ${disabled}>${i}</button>`;
    }

    // Last page
    if (end < totalPages) {
      if (end < totalPages - 1) {
        pagesHtml += `<span class="design-resources-pagination__ellipsis">...</span>`;
      }
      pagesHtml += `<button class="design-resources-pagination__page" data-action="goto-page" data-page="${totalPages}">${totalPages}</button>`;
    }

    const html = `
      <div class="design-resources-pagination__inner">
        <button class="design-resources-pagination__btn design-resources-pagination__btn--prev" data-action="prev-page" ${current <= 1 ? 'disabled' : ''}>
          <span class="design-resources-pagination__btn-text"></span>
        </button>
        <div class="design-resources-pagination__pages">
          ${pagesHtml}
        </div>
        <button class="design-resources-pagination__btn design-resources-pagination__btn--next" data-action="next-page" ${current >= totalPages ? 'disabled' : ''}>
          <span class="design-resources-pagination__btn-text"></span>
        </button>
      </div>
    `;

    elements.pagination.innerHTML = html;

    // Attach pagination listeners
    attachPaginationListeners();
  }

  /**
   * Attach event listeners to pagination buttons
   */
  function attachPaginationListeners() {
    const prevBtn = elements.pagination.querySelector('[data-action="prev-page"]');
    const nextBtn = elements.pagination.querySelector('[data-action="next-page"]');
    const pageButtons = elements.pagination.querySelectorAll('[data-action="goto-page"]');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        if (state.currentPage > 1) {
          state.currentPage--;
          renderResources();
          renderPagination();
          scrollToTop();
        }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        const totalPages = Math.ceil(state.filteredResources.length / state.perPage);
        if (state.currentPage < totalPages) {
          state.currentPage++;
          renderResources();
          renderPagination();
          scrollToTop();
        }
      });
    }

    pageButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const page = parseInt(btn.dataset.page, 10);
        if (page !== state.currentPage) {
          state.currentPage = page;
          renderResources();
          renderPagination();
          scrollToTop();
        }
      });
    });
  }

  /**
   * Scroll to top of component
   */
  function scrollToTop() {
    component.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * Toggle resource type selection
   */
  function toggleType(typeId) {
    const index = state.selectedTypes.indexOf(typeId);

    if (index > -1) {
      state.selectedTypes.splice(index, 1);
    } else {
      state.selectedTypes.push(typeId);
    }

    // Update button state
    updateTypeButtonState(typeId);

    // Apply filters and re-render
    applyFilters();
    renderResources();
    renderPagination();
  }

  /**
   * Update type button visual state
   */
  function updateTypeButtonState(typeId) {
    const btn = elements.resourceTypes.querySelector(`[data-type-id="${typeId}"]`);
    if (btn) {
      if (state.selectedTypes.includes(typeId)) {
        btn.classList.add('design-resources-filter__type-btn--active');
      } else {
        btn.classList.remove('design-resources-filter__type-btn--active');
      }
    }
  }

  /**
   * Add tag to selection
   */
  function addTag(tagId, tagName) {
    if (state.selectedTags.includes(tagId)) return;

    state.selectedTags.push(tagId);
    renderSelectedTags();

    // Apply filters and re-render
    applyFilters();
    renderResources();
    renderPagination();

    // Clear search input
    if (elements.tagSearchInput) {
      elements.tagSearchInput.value = '';
    }
    clearTagSuggestions();
  }

  /**
   * Remove tag from selection
   */
  function removeTag(tagId) {
    const index = state.selectedTags.indexOf(tagId);
    if (index > -1) {
      state.selectedTags.splice(index, 1);
      renderSelectedTags();

      // Apply filters and re-render
      applyFilters();
      renderResources();
      renderPagination();
    }
  }

  /**
   * Render selected tags
   */
  function renderSelectedTags() {
    if (!elements.selectedTags || !state.filterData) return;

    const tags = state.filterData.resource_tags.filter((tag) =>
      state.selectedTags.includes(tag.id)
    );

    if (tags.length === 0) {
      elements.selectedTags.innerHTML = '';
      return;
    }

    const html = tags
      .map(
        (tag) => `
      <span class="design-resources-filter__selected-tag" data-tag-id="${tag.id}">
        <button class="design-resources-filter__remove-tag" data-action="remove-tag" data-tag-id="${tag.id}" aria-label="Remove ${tag.name}">
          ${tag.name}
          &times;
        </button>
      </span>
    `
      )
      .join('');

    elements.selectedTags.innerHTML = html;

    // Attach remove listeners
    elements.selectedTags.querySelectorAll('[data-action="remove-tag"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tagId = parseInt(btn.dataset.tagId, 10);
        removeTag(tagId);
      });
    });
  }

  /**
   * Search tags and show suggestions
   */
  function searchTags(query) {
    if (!state.filterData || !query) {
      clearTagSuggestions();
      return;
    }

    const lowerQuery = query.toLowerCase();
    const suggestions = state.filterData.resource_tags
      .filter(
        (tag) => tag.name.toLowerCase().includes(lowerQuery) && !state.selectedTags.includes(tag.id)
      )
      .slice(0, 10);

    if (suggestions.length === 0) {
      clearTagSuggestions();
      return;
    }

    const html = suggestions
      .map(
        (tag) => `
      <button class="design-resources-filter__tag-suggestion" data-action="add-tag" data-tag-id="${tag.id}" data-tag-name="${tag.name}">
        ${tag.name}
      </button>
    `
      )
      .join('');

    elements.tagSuggestions.innerHTML = html;
    elements.tagSuggestions.style.display = 'block';

    // Attach add listeners
    elements.tagSuggestions.querySelectorAll('[data-action="add-tag"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tagId = parseInt(btn.dataset.tagId, 10);
        const tagName = btn.dataset.tagName;
        addTag(tagId, tagName);
      });
    });
  }

  /**
   * Clear tag suggestions
   */
  function clearTagSuggestions() {
    if (elements.tagSuggestions) {
      elements.tagSuggestions.innerHTML = '';
      elements.tagSuggestions.style.display = 'none';
    }
  }

  function togglePopup() {
    if (!elements.popup) return;
    elements.popup.classList.toggle('open');
  }

  function showFormPopup(resource) {
    if (!elements.popup) return;

    // Update button text based on type
    const submitBtn = elements.popupForm?.querySelector('[type="submit"]');
    if (submitBtn) {
      if (resource.link_type === 'request') {
        submitBtn.textContent = 'Request Access';
      } else {
        submitBtn.textContent = 'Download';
      }
    }

    elements.popup.classList.add('open');
    elements.popup.scrollIntoView({ behavior: 'auto' });
  }

  function closeFormPopup() {
    if (!elements.popup) return;
    elements.popup.classList.remove('open');
    if (elements.popupForm) {
      elements.popupForm.reset();
      resetTurnstile();
    }
  }

  function getTurnstileToken() {
    const turnstileEl = elements.popupForm?.querySelector('[data-element="turnstile"]');
    if (!turnstileEl) return '';

    // Get the response token from Turnstile
    const response = turnstileEl.querySelector('input[name="cf-turnstile-response"]');
    return response ? response.value : '';
  }

  function resetTurnstile() {
    if (typeof turnstile !== 'undefined') {
      const turnstileEl = elements.popupForm?.querySelector('[data-element="turnstile"]');
      if (turnstileEl) {
        turnstile.reset(turnstileEl);
      }
    }
  }

  async function handleFreeDownload(resource) {
    // Direct download for free resources
    const downloadUrl = `${SITE_URL}/wp-json/empower/v1/download/free/${resource.id}`;
    window.location.href = downloadUrl;
  }

  async function handleLeadSubmit(data) {
    console.log('Submitting lead form with data:', data);

    const response = await fetch(`${SITE_URL}/wp-json/empower/v1/download/lead`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();
    console.log('Lead submission response:', result);

    if (result.success) {
      console.log('Redirecting to:', result.redirect_url);
      console.log('Hash:', result.hash);
      // Redirect to Thank You page with hash
      window.location.href = result.redirect_url;
    } else {
      alert(result.message || 'An error occurred. Please try again.');
      resetTurnstile();
    }
  }

  async function handleRequestSubmit(data) {
    console.log('Submitting request form with data:', data);

    const response = await fetch(`${SITE_URL}/wp-json/empower/v1/download/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();
    console.log('Request submission response:', result);

    if (result.success) {
      console.log('Redirecting to:', result.redirect_url);
      // Redirect to Thank You page (no hash for request type)
      window.location.href = result.redirect_url;
    } else {
      alert(result.message || 'An error occurred. Please try again.');
      resetTurnstile();
    }
  }

  /**
   * Initialize event listeners
   */
  function init() {
    // Filter toggle
    if (elements.filterBtn) {
      elements.filterBtn.addEventListener('click', function () {
        this.classList.toggle('opened');
        toggleFilter();
      });
    }

    // Close filter
    if (elements.closeFilterBtn) {
      elements.closeFilterBtn.addEventListener('click', closeFilter);
    }

    // Tag search input
    if (elements.tagSearchInput) {
      let searchTimeout;
      elements.tagSearchInput.addEventListener('input', (e) => {
        clearTimeout(searchTimeout);
        const query = e.target.value.trim();
        searchTimeout = setTimeout(() => {
          searchTags(query);
        }, 300);
      });

      // Close suggestions when clicking outside
      document.addEventListener('click', (e) => {
        if (!elements.tagSuggestions.contains(e.target) && e.target !== elements.tagSearchInput) {
          clearTagSuggestions();
        }
      });

      // the same overlay click
    }

    // Handle download buttons
    if (elements.resourcesList) {
      let currentResource = null;

      elements.resourcesList.addEventListener('click', (e) => {
        const btn = e.target.closest('.design-resource-item__btn');
        if (!btn) return;

        const resourceItem = btn.closest('.design-resource-item');
        const resourceId = resourceItem.dataset.resourceId || null;

        if (!resourceId) return;

        // Find resource data from state
        const resource = state.allResources.find((r) => r.id == resourceId);
        if (!resource) return;

        e.preventDefault();

        console.log('Resource clicked:', {
          id: resource.id,
          title: resource.title,
          link_type: resource.link_type,
        });

        // Handle based on link_type
        if (resource.link_type === 'download') {
          // Free download - direct download
          console.log('Handling as FREE download');
          handleFreeDownload(resource);
        } else if (resource.link_type === 'lead' || resource.link_type === 'request') {
          // Show form for lead or request
          console.log('Showing form for type:', resource.link_type);
          currentResource = resource;
          showFormPopup(resource);
        }
      });

      // Close popup buttons
      if (elements.popup) {
        const closeBtns = elements.popup.querySelectorAll('[data-action="close-popup"]');
        if (closeBtns) {
          closeBtns.forEach((btn) => {
            btn.addEventListener('click', () => {
              closeFormPopup();
              currentResource = null;
            });
          });
        }
      }

      // Form submission
      if (elements.popupForm) {
        elements.popupForm.addEventListener('submit', async (e) => {
          e.preventDefault();

          if (!currentResource) return;

          const formData = new FormData(elements.popupForm);
          const submitBtn = elements.popupForm.querySelector('[type="submit"]');

          // Get Turnstile token
          const turnstileResponse = getTurnstileToken();

          const data = {
            resource_id: currentResource.id,
            first_name: formData.get('first_name'),
            last_name: formData.get('last_name'),
            email: formData.get('email'),
            organization: formData.get('organization'),
            'cf-turnstile-response': turnstileResponse,
          };

          // Disable submit button
          submitBtn.disabled = true;
          submitBtn.textContent = 'Processing...';

          try {
            console.log('Submitting form for resource type:', currentResource.link_type);

            if (currentResource.link_type === 'lead') {
              console.log('Calling LEAD endpoint');
              await handleLeadSubmit(data);
            } else if (currentResource.link_type === 'request') {
              console.log('Calling REQUEST endpoint');
              await handleRequestSubmit(data);
            } else {
              console.error('Unknown resource type:', currentResource.link_type);
            }
          } catch (error) {
            console.error('Form submission error:', error);
            alert('An error occurred. Please try again.');
            resetTurnstile();
          } finally {
            submitBtn.disabled = false;
            submitBtn.textContent =
              currentResource.link_type === 'request' ? 'Request Access' : 'Download';
          }
        });
      }

      elements.resourcesList.addEventListener('click', function (event) {
        if (!event.target.closest('.design-resource-item__thumbnail')?.parentElement) return;

        const isVideo = event.target
          .closest('.design-resource-item__thumbnail')
          .parentElement.classList.contains('with-popup--video');

        if (!isVideo) return;
        if (isVideo) {
          event.preventDefault();

          // data-element="video-popup"
          const videoPopup = document.querySelector('[data-element="video-popup"]');
          videoPopup.classList.toggle('open');

          // create video element in data-element="video-container"
          const videoContainer = videoPopup.querySelector('[data-element="video-container"]');
          const videoUrl = event.target.closest('.design-resource-item__thumbnail-link').href;

          // video source already set as child of videoContainer
          videoContainer.innerHTML = `
            <video controls autoplay muted>
              <source src="${videoUrl}" type="video/mp4">
              Your browser does not support the video tag.
            </video>
          `;
        }

        if (elements.popup) {
          const closeVideoBtns = document.querySelectorAll('[data-action="close-video-popup"]');

          if (closeVideoBtns) {
            closeVideoBtns.forEach((btn) => {
              btn.addEventListener('click', () => {
                const videoPopup = document.querySelector('[data-element="video-popup"]');
                videoPopup.classList.remove('open');

                // Remove video element to stop playback
                const videoContainer = videoPopup.querySelector('[data-element="video-container"]');
                videoContainer.innerHTML = '';
              });
            });
          }
        }
      });
    }

    // Fetch all data from REST API
    fetchAllData();
  }

  // Initialize
  init();
}
